// Ported from 260511_search_test/frontend/app/lib.ts — source badges,
// region list, external-results filter expression for the integrated SERP.

export type BadgeStyle = { bg: string; text: string }

const BADGES: Record<string, BadgeStyle> = {
	Internetmedicin: { bg: 'bg-green-100', text: 'text-green-800' },

	// Myndigheter
	LV: { bg: 'bg-purple-100', text: 'text-purple-800' },
	LMB: { bg: 'bg-purple-100', text: 'text-purple-800' },
	'SoS FMB': { bg: 'bg-purple-100', text: 'text-purple-800' },
	'SoS Riktlinjer': { bg: 'bg-purple-100', text: 'text-purple-800' },
	FoHM: { bg: 'bg-purple-100', text: 'text-purple-800' },

	// Nationella vårdprogram
	RCC: { bg: 'bg-blue-100', text: 'text-blue-800' },
	'1177': { bg: 'bg-blue-100', text: 'text-blue-800' },

	// Regional läkemedelslista
	Janusinfo: { bg: 'bg-amber-100', text: 'text-amber-800' },
	RMR: { bg: 'bg-amber-100', text: 'text-amber-800' },
	Skånelistan: { bg: 'bg-amber-100', text: 'text-amber-800' },
	'Region Uppsala': { bg: 'bg-amber-100', text: 'text-amber-800' },
	VISS: { bg: 'bg-amber-100', text: 'text-amber-800' },
}

const DEFAULT_BADGE: BadgeStyle = {
	bg: 'bg-orange-100',
	text: 'text-orange-800',
}

export function badgeFor(source: string): BadgeStyle {
	return BADGES[source] ?? DEFAULT_BADGE
}

// NOTE: an earlier revision coloured Snabbsök results by source_type CATEGORY
// (5 buckets) via `sourceTypeStyle`/`sourceTypeBadge`/`sourceTypeLabel`. The
// user chose PER-SOURCE colours instead (see `sourceColor` below), so the
// category helpers were removed as dead code. The category approach is recorded
// in the design report + git history (R3) if it's ever wanted again.

/**
 * Short display name for a source abbreviation shown on the coloured block.
 * Most source values are already short (LMB, 1177, RCC, SFOG …) and pass
 * through unchanged. A few are full org names too long for the compact block
 * ("Svensk Urologisk Forening", "Endokrinologforeningen") — map those to a
 * tight abbreviation so the block stays legible instead of truncating to
 * "Svensk Urologis…". The full name still shows on hover (title attr).
 */
const SHORT_SOURCE_NAMES: Record<string, string> = {
	'SoS Riktlinjer': 'SoS NR', // Socialstyrelsen nationella riktlinjer
	'Svensk Urologisk Forening': 'SUF',
	Endokrinologforeningen: 'SEF',
	'Svensk Barnkirurgisk Forening': 'SBKF',
	'Svensk Thoraxkirurgisk Forening': 'STF',
	'Svensk Forening for Beroendemedicin': 'SFBM',
	'Svensk Forening for Thoraxradiologi': 'SFTR',
	'Svensk ONH-forening': 'ONH',
	'Nord Hemophilia': 'NHC',
	'MS-sällskapet': 'MS-säll.',
	'Region Uppsala': 'Reg. Upps.',
	'Region Skåne': 'Reg. Skåne',
}

export function shortSourceName(source: string | undefined): string {
	if (!source) return ''
	return SHORT_SOURCE_NAMES[source] ?? source
}

// ───────────────────────────────────────────────────────────────────────────
// PER-SOURCE colour (Snabbsök R3, user-requested 2026-05-21). The user wanted
// each source to have its OWN distinct colour ("so you can very quickly see
// which source is which") rather than the 5 source_type categories. Returns a
// hex string for the chip background; the chip uses white text, so every hue
// here is a Tailwind -700/-800 shade verified ≥4.5:1 contrast on white (WCAG
// AA). The ~18 highest-volume / most-recognisable sources get hand-picked
// distinct hues; the long tail of small specialist societies gets a stable
// deterministic hash→hue from a ring that EXCLUDES the curated hues (so a tail
// source can never render in, say, Internetmedicin's green or Region Skåne's
// red — team-lead fix). Abbrev text on the chip still gives the exact source.
//
// GREEN IS RESERVED FOR INTERNETMEDICIN (IM-82, decided 2026-10-09): no
// external source — curated or hashed — may get a green, lime or teal hue (HSL
// hue 60–185°). Teal is out too: next to IM green it reads as green. VISS, RMR
// and Infektion.net moved to -900 shades of families already in use (no free
// non-green family was left), so they are distinct by shade, not by family.
// Enforced by search-lib.test.ts. The site gets these colours through the API
// (`source_color` on /api/v1/search, `color` in the chat's sources frame), so
// a change here reaches both products without a copy on the site.
export const SOURCE_COLORS: Record<string, string> = {
	Internetmedicin: '#4e7a3f', // im green-600 (user-confirmed IM primary)
	'Region Skåne': '#b91c1c', // red-700
	'Region Uppsala': '#be123c', // rose-700
	'1177': '#1d4ed8', // blue-700
	SFOG: '#be185d', // pink-700
	RCC: '#a21caf', // fuchsia-700
	BLF: '#c2410c', // orange-700
	VISS: '#0c4a6e', // sky-900
	LMB: '#6d28d9', // violet-700
	LV: '#7e22ce', // purple-700
	'SoS FMB': '#4338ca', // indigo-700
	SGF: '#b45309', // amber-700
	Janusinfo: '#0e7490', // cyan-700
	'Infektion.net': '#1e3a8a', // blue-900
	Skånelistan: '#0369a1', // sky-700
	RMR: '#7c2d12', // orange-900
	FoHM: '#a16207', // yellow-700
	'SoS Riktlinjer': '#2563eb', // blue-600
}

// Hash-fallback ring: AA-safe -700/-800/-900 shades, no hex shared with
// SOURCE_COLORS above, so a hashed tail source never gets exactly a curated
// major source's colour (neighbouring shades of one family do occur).
// Keep the LENGTH stable: it is the hash modulus, so adding or removing a slot
// recolours every tail source. The three -900 slots replaced teal/green/lime
// -800 in place (IM-82), which only moved the sources that hashed there.
export const SOURCE_COLOR_RING: string[] = [
	'#9f1239', // rose-800
	'#9d174d', // pink-800
	'#86198f', // fuchsia-800
	'#6b21a8', // purple-800
	'#5b21b6', // violet-800
	'#3730a3', // indigo-800
	'#1e40af', // blue-800
	'#075985', // sky-800
	'#155e75', // cyan-800
	'#713f12', // yellow-900
	'#831843', // pink-900
	'#312e81', // indigo-900
	'#854d0e', // yellow-800
	'#92400e', // amber-800
	'#9a3412', // orange-800
	'#991b1b', // red-800
	'#3f3f46', // zinc-700
	'#44403c', // stone-700
	'#334155', // slate-700
]

const DEFAULT_SOURCE_COLOR = '#475569' // slate-600

function hashString(s: string): number {
	let x = 0
	for (let i = 0; i < s.length; i++) x = (x * 31 + s.charCodeAt(i)) & 0xffffffff
	return x >>> 0
}

/**
 * Distinct chip background colour (hex) for a source. Curated hue for the major
 * sources; stable hash→ring hue (disjoint from the curated set) for the long
 * tail. White text on the returned colour meets WCAG AA. Used with an inline
 * `style={{ backgroundColor }}` on the chip (hash hues can't be Tailwind class
 * names). The abbrev text identifies the exact source.
 */
export function sourceColor(source: string | undefined): string {
	if (!source) return DEFAULT_SOURCE_COLOR
	const curated = SOURCE_COLORS[source]
	if (curated) return curated
	return SOURCE_COLOR_RING[hashString(source) % SOURCE_COLOR_RING.length]
}

/**
 * Full, spelled-out source name for the desktop hover preview (R3 #32). The
 * chip shows a terse abbrev (LMB / SoS FMB / RAF …); the hover spells it out so
 * a clinician who doesn't recognise the abbrev still knows the source. Falls
 * back to the source value itself when we don't have a longer form.
 */
const SOURCE_FULL_NAMES: Record<string, string> = {
	Internetmedicin: 'Internetmedicin',
	'1177': '1177 Vårdpersonal',
	LMB: 'Läkemedelsboken',
	LV: 'Läkemedelsverket',
	'SoS FMB': 'Socialstyrelsen — försäkringsmedicinskt beslutsstöd',
	'SoS Riktlinjer': 'Socialstyrelsen — nationella riktlinjer',
	FoHM: 'Folkhälsomyndigheten',
	RCC: 'Regionala cancercentrum',
	Janusinfo: 'Janusinfo (Region Stockholm)',
	RMR: 'Regionala medicinska riktlinjer, Västra Götalandsregionen',
	Skånelistan: 'Skånelistan (Region Skåne)',
	VISS: 'VISS — vårdgivarstöd (Region Stockholm)',
	'Region Skåne': 'Region Skåne',
	'Region Uppsala': 'Region Uppsala',
	SFOG: 'Svensk Förening för Obstetrik och Gynekologi',
	BLF: 'Svenska Barnläkarföreningen',
	SGF: 'Svensk Gastroenterologisk Förening',
	SLMF: 'Svensk Lungmedicinsk Förening',
	SRF: 'Svensk Reumatologisk Förening',
	RAF: 'Referensgruppen för Antibiotikafrågor',
	'Infektion.net': 'Infektion.net (Svenska Infektionsläkarföreningen)',
	SFAI: 'Svensk Förening för Anestesi och Intensivvård',
	SFHem: 'Svensk Förening för Hematologi',
	SSDV: 'Svenska Sällskapet för Dermatologi och Venereologi',
	SFMG: 'Svensk Förening för Medicinsk Genetik',
	SFBUP: 'Svenska Föreningen för Barn- och Ungdomspsykiatri',
	Njurmed: 'Svensk Njurmedicinsk Förening',
	'MS-sällskapet': 'Svenska MS-sällskapet',
	'Nord Hemophilia': 'Nordic Hemophilia Council',
	SNEMA: 'Svenska Neuromuskulära Arbetsgruppen',
	SSTH: 'Svenska Sällskapet för Trombos och Hemostas',
	Swedeye: 'Swedeye (Sveriges Ögonläkarförening)',
	SVOS: 'Svenska Osteoporossällskapet',
	SURF: 'Svensk Urogenitalradiologisk Förening (SURF)',
	RAM: 'Referensgruppen för Antimykotisk Terapi',
	'SVKF GUCH': 'Svensk Kardiologisk Förening (GUCH)',
	'Svensk Urologisk Forening': 'Svensk Urologisk Förening',
	SFFA: 'Svenska Föreningen för Allergologi',
	Enuresakademin: 'Enuresakademin',
	HVS: 'Huvudvärkssällskapet',
	RAV: 'Referensgruppen för Antiviral Terapi',
	SWEMODIS: 'Swedish Movement Disorder Society',
}

export function sourceFullName(source: string | undefined): string {
	if (!source) return ''
	return SOURCE_FULL_NAMES[source] ?? source
}

export const REGIONS: { value: string; label: string }[] = [
	{ value: 'all', label: 'Alla regioner' },
	{ value: 'stockholm', label: 'Region Stockholm' },
	{ value: 'vgr', label: 'Region Västra Götaland' },
	{ value: 'skane', label: 'Region Skåne' },
	{ value: 'uppsala', label: 'Region Uppsala' },
]

/**
 * "Which documents belong to the selected region", as a Typesense filter clause:
 * the region's own content plus everything national — region=national (all IM
 * articles, plus the national sources) and regional documents explicitly flagged
 * `national_use` (VGR:s RMR-riktlinjer m.fl.). Other regions' content is what
 * gets excluded. Empty string for 'all': no region selected = no scoping.
 *
 * The clause mirrors the R1/R2 ranking semantics in snabbsok.ts — in-region hits
 * get the strong bonus, national keeps a smaller one, other regions get nothing.
 * Live caller: Snabbsök's vector-lane hydration (snabbsok.ts, vecLaneFetch).
 *
 * `region` is interpolated into the filter string, so any value outside the
 * REGIONS allowlist collapses to '' — same behaviour as the route's own
 * unknown-region fallback to 'all' (resources+/snabbsok.ts), just enforced
 * where the interpolation actually happens.
 */
export function regionScopeFilter(region: string): string {
	if (region === 'all') return ''
	if (!REGIONS.some(r => r.value === region)) return ''
	// TWO-SCHEMA CONTRACT: under SNABBSOK_VEC_DENORM this clause also runs
	// against the denormalised vector collection, so every field named here must
	// exist in BOTH im_search and that collection. Add a field → rebuild the
	// vector collection (tools/search/build-vec-denorm.mjs) or every regional
	// user's kNN answers 400.
	return `(region:=[${region},national] || national_use:=true)`
}

// Strip metadata + markdown + URLs from raw content fields. Mirrors the
// indexer's clean_display_content() so snippets render as clean prose even
// when title/content fields leak through.
const METADATA_PREFIX = /^(?:\s*\[[^\]\n:]+:\s*[^\]\n]*\]\s*)+/
const MARKDOWN_LINK = /\[([^\]]+)\]\((https?:[^)]+)\)/g
const BARE_URL = /https?:\/\/\S+/g
const CODE_REF = /\[\s*[A-Z]\d{1,3}(?:\.\d+)?\s*\]/g

export function cleanDisplay(text: string | undefined | null): string {
	if (!text) return ''
	return text
		.replace(METADATA_PREFIX, '')
		.replace(MARKDOWN_LINK, '$1')
		.replace(BARE_URL, '')
		.replace(CODE_REF, '')
		.replace(/`([^`]+)`/g, '$1')
		.replace(/\s+/g, ' ')
		.trim()
}

// Split "Article Title - Section" into base title + section.
export function splitTitleSection(title: string): {
	page: string
	section: string | null
} {
	const i1 = title.lastIndexOf(' – ')
	const i2 = title.lastIndexOf(' - ')
	const idx = Math.max(i1, i2)
	if (idx > 0) {
		return {
			page: title.slice(0, idx).trim(),
			section: title.slice(idx + 3).trim(),
		}
	}
	return { page: title.trim(), section: null }
}

export function formatSwedishDate(iso?: string | null): string | null {
	if (!iso) return null
	const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
	if (!m) return null
	const months = [
		'jan',
		'feb',
		'mar',
		'apr',
		'maj',
		'jun',
		'jul',
		'aug',
		'sep',
		'okt',
		'nov',
		'dec',
	]
	return `${parseInt(m[3], 10)} ${months[parseInt(m[2], 10) - 1]} ${m[1]}`
}
